"use client";

import { useGetBanks } from "@/hooks/main-hook";
import { useTranslations, useLocale } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { ArrowUpRight, Landmark, Boxes, Star } from "lucide-react";

export default function Banks() {
  const t = useTranslations("Home");
  const locale = useLocale();
  const isRtl = locale === "ar";

  const { data, isLoading } = useGetBanks({ page: 1, limit: 4 });
  const banks = data?.banks || [];

  return (
    <div className="py-24 bg-[#F8FAFC] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-150 h-150 bg-main/5 blur-[120px] rounded-full -z-10" />
      <div className="absolute bottom-0 left-0 w-100 h-100 bg-blue-500/5 blur-[100px] rounded-full -z-10" />

      <div className="container px-4 md:px-0 lg:w-[85%] mx-auto relative z-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-20">
          <div className="space-y-5">
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 px-4 py-2 rounded-2xl shadow-sm">
              <Landmark className="text-main w-4 h-4" />
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                {isRtl ? "الشركاء الاستراتيجيون" : "Strategic Partners"}
              </span>
            </div>
            <h2 className="text-4xl md:text-6xl font-black text-slate-900 tracking-tight leading-none">
              {isRtl ? "البنوك" : "Banks"}{" "}
              <span className="text-main font-light italic">
                {isRtl ? "المعتمدة" : "Strategic"}
              </span>
            </h2>
          </div>

          <Link
            href="/banks"
            className="group flex items-center gap-4 bg-slate-900 text-white px-10 py-5 rounded-4xl font-black text-sm hover:bg-main transition-all duration-500 shadow-2xl shadow-slate-200"
          >
            {t("viewAll")}
            <Boxes className="w-5 h-5 group-hover:rotate-30 transition-transform duration-500" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {isLoading
            ? [1, 2, 3, 4].map((_, index) => <BankSkeleton key={index} />)
            : banks.slice(0, 4).map((bank: any) => (
                <Link
                  href={`/banks/${bank.slug}`}
                  key={bank.id}
                  className="group block space-y-6"
                >
                  <div className="relative h-80 w-full bg-white rounded-[3rem] overflow-hidden border border-slate-100 shadow-sm transition-all duration-700 group-hover:shadow-[0_40px_80px_-20px_rgba(0,0,0,0.1)] group-hover:-translate-y-4">
                    <div className="absolute inset-0 z-0 bg-slate-50">
                      <Image
                        src={bank.image}
                        alt={bank?.name_en}
                        fill
                        className="object-cover transition-all duration-1000 group-hover:scale-110"
                      />
                    </div>

                    <div className="absolute inset-0 z-10 bg-black/0 group-hover:bg-black/10 transition-all duration-700" />

                    <div className="absolute top-6 right-6 z-20">
                      <div className="bg-white/90 backdrop-blur-md p-3 rounded-2xl shadow-sm opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2.5 group-hover:translate-y-0">
                        <ArrowUpRight className="w-5 h-5 text-main" />
                      </div>
                    </div>

                    <div className="absolute bottom-6 left-6 z-20">
                      <div className="bg-main text-white px-4 py-1.5 rounded-full text-[8px] font-black uppercase tracking-widest flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2.5 group-hover:translate-y-0">
                        <Star size={10} fill="currentColor" />
                        {isRtl ? "موثوق" : "Verified"}
                      </div>
                    </div>
                  </div>

                  <div className="px-2 space-y-1 transition-all duration-500 group-hover:px-4">
                    <h3 className="text-xl font-black text-slate-900 group-hover:text-main transition-colors duration-300">
                      {isRtl ? bank.name_ar || bank.name_en : bank.name_en}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        {isRtl ? "برامج التمويل العقاري" : "Mortgage Programs"}
                      </span>
                      <div className="h-0.5 flex-1 bg-slate-100 group-hover:bg-main/20 transition-colors" />
                    </div>
                  </div>
                </Link>
              ))}
        </div>

        <div className="mt-24 pt-12 border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-main/10 flex items-center justify-center text-main">
              <Landmark size={20} />
            </div>
            <p className="text-slate-500 font-bold text-sm max-w-md leading-relaxed">
              {isRtl
                ? "جميع البنوك مرخصة ومراقبة من البنك المركزي لضمان أمان معاملاتكم"
                : "All partners are licensed and regulated by the Central Bank to ensure security"}
            </p>
          </div>
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl border border-slate-100 shadow-sm">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[10px] font-black text-slate-800 uppercase tracking-widest">
              Live Network Status
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

const BankSkeleton = () => {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-80 bg-slate-100 rounded-[3rem]" />
      <div className="space-y-2 px-2">
        <div className="h-6 w-2/3 bg-slate-100 rounded-lg" />
        <div className="h-3 w-1/2 bg-slate-50 rounded-lg" />
      </div>
    </div>
  );
};
